import React from 'react';
import styled from '@emotion/styled';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import useUserLocation from 'hooks/useUserLocation';

import MainLayout from 'layouts/MainLayout';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Helmet from 'components/Helmet';
import PageContent from 'components/PageContent';
import PageTitle from 'components/PageTitle';
import LinearProgress from 'components/LinearProgress';
import BgLeft from 'assets/icons/download5.svg?react';
import BgRight from 'assets/icons/download6.svg?react';
import TrollGreetings from 'assets/images/careerTest/troll-greetings.png';
import EducationProgramsEndPoint from 'endPoints/educationPrograms';
import useDataListFactory from 'services/dataList';
import EducationProgramCard from './components/EducationProgramCard';
import { useLocation, useNavigate } from 'react-router-dom';
import parseQS from 'helpers/parseQS';
import slugify from '@sindresorhus/slugify';

import FiltersIcon from 'assets/icons/filters.svg?react';
import CheckboxIcon from 'assets/icons/checkbox.svg?react';
import SortIcon from 'assets/icons/iconoir_sort.svg?react';
import SearchToggleInput from 'components/SearchToggleInput';
import { ResultsWrapper, ResultsControls } from 'components/ResultsLayout';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import { useDispatch } from 'react-redux';
import { getEducationProgramsFilterOptions } from 'actions/educations';
import { getLanguage } from 'translation';
import CardGridSkeleton from 'components/CardGridSkeleton';

const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const useDataList = (props, options) => useDataListFactory(props, options);

const Title = styled(PageTitle)({});

const Background = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.tertiary.pale,
  position: 'relative'
}));

const BgLeftStyled = styled(BgLeft)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TrollGreetingsStyled = styled('img')(({ theme }) => ({
  position: 'absolute',
  bottom: -100,
  right: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TrollGreetingsStyledTop = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 70,
  right: -120,
  transform: 'rotate(-45deg)',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const BgRightStyled = styled(BgRight)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  bottom: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const Content = styled(PageContent)(({ theme }) => ({
  paddingBottom: 32,
  paddingTop: 30,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('lg')]: {
    paddingLeft: 20,
    paddingRight: 20
  },
  [theme.breakpoints.down('sm')]: {
    paddingBottom: 40
  }
}));

const Card = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: 18,
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: '36px 24px 26px',
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1
}));

const List = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 24,
  marginBottom: 8,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(1, 1fr)',
    gap: 16
  }
}));

const ProgramsGroup = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 24,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(1, 1fr)',
    gap: 16
  }
}));

const TagsButton = styled(Button)(({ variant, theme }) => ({
  padding: '7px 17px',
  border: '1px solid rgba(10, 10, 10, 1)',
  ...(variant === 'contained'
    ? {
        backgroundColor: theme.palette.primary.salad,
        color: theme.palette.textPrimary.main,
        '&:hover': { backgroundColor: theme.palette.primary.main }
      }
    : {
        backgroundColor: 'transparent',
        color: 'inherit',
        '&:hover': { backgroundColor: 'transparent' }
      })
}));

const DirectionsContainer = styled('div')(({ theme, showAll, maxRows, isMobile }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: showAll ? 16 : 0,
  overflow: 'hidden',
  maxHeight: showAll ? 'none' : isMobile ? '432px' : '180px', // ~36px per row (button height + gap)
  position: 'relative'
}));

const ShowAllButton = styled(Button)(() => ({
  marginTop: 8,
  textTransform: 'none',
  fontSize: 14,
  fontWeight: 500
}));

const ResultsLabel = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  paddingLeft: 3,
  color: theme.palette.textTertiary.main
}));

const ResultsLabelContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8
}));

const Filters = styled('div')(() => ({
  borderRadius: 18,
  backgroundColor: 'rgba(121, 201, 18, 0.1)',
  padding: 24,
  paddingTop: 16,
  paddingBottom: 16
}));

const FiltersTitle = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 500,
  marginBottom: 12,
  display: 'flex',
  alignItems: 'center',
  '& svg': { marginRight: 16 },
  [theme.breakpoints.down('sm')]: {
    cursor: 'pointer',
    marginBottom: 0
  }
}));

const FiltersHeadline = styled(Typography)(() => ({
  fontSize: 16,
  fontWeight: 500,
  marginBottom: 0,
  marginTop: 16,
  marginLeft: -2
}));

const CheckboxStyled = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: 3,
  marginLeft: 8,
  width: 21,
  height: 21,
  '& svg': {
    width: 21,
    height: 21
  }
}));

const FormGroupStyled = styled(FormGroup)(() => ({ 
  marginBottom: 0,
  marginTop: 0,
  '& + &': {
    marginTop: -4
  }
}));

const FormControlLabelStyled = styled(FormControlLabel)(() => ({
  fontSize: 14,
  marginBottom: 0,
  marginTop: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: 14,
    paddingTop: 2
  }
}));

const IconButtonStyled = styled(IconButton)(() => ({
  padding: 0,
  margin: 3
}));

const FilterWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: { marginTop: 24 }
}));

const BreadcrumbsWrapper = styled('div')(() => ({
  marginBottom: 24
}));

const Actions = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 16,
  gap: 16,
  '& > *:last-child': {
    flex: 1,
    maxWidth: '100%'
  }
}));

const ClearFilterActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  fontSize: 12,
  fontFamily: 'Unbounded',
  padding: '10px 31px',
  border: `1px solid ${theme.palette.grey[600]}`,
  marginTop: 12,
  width: '100%',
  backgroundColor: theme.palette.white.main,
  color: theme.palette.grey[600],
  zIndex: 10
}));

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 24,
  [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' }
}));

const LeftSidebar = styled('div')(() => ({ marginBottom: 20, flex: 2 }));

const RightSidebar = styled('div')(({ theme }) => ({
  flex: 1,
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    paddingRight: 0
  }
}));

const InstitutionGroupWrapper = styled('div')(() => ({
  marginBottom: 32
}));

const InstitutionHeader = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: 18,
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: '20px 24px',
  marginBottom: 16,
  cursor: 'pointer'
}));

const InstitutionName = styled(Typography)(() => ({
  fontSize: 20,
  fontWeight: 500,
  color: 'rgba(10, 10, 10, 1)',
  marginBottom: 4
}));

const InstitutionInfo = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.textTertiary.main
}));

// Component to display institution group with its programs
const InstitutionGroup = ({ institution, institutionType, children }) => {
  const navigate = useNavigate();
  const lang = getLanguage().toUpperCase();
  
  // Helper to extract name from potentially localized object
  const getInstitutionName = React.useCallback((inst) => {
    if (!inst) return null;
    
    // If name is a string, use it directly
    if (typeof inst.name === 'string' && inst.name.trim()) {
      return inst.name;
    }
    
    // If name is an object with language keys, extract the appropriate one
    if (inst.name && typeof inst.name === 'object') {
      const langLower = lang.toLowerCase();
      if (inst.name[langLower]) return inst.name[langLower];
      if (inst.name[lang]) return inst.name[lang];
      // Fallback to any available language
      const firstLang = Object.keys(inst.name)[0];
      if (firstLang && inst.name[firstLang]) return inst.name[firstLang];
    }
    
    // Try localized name property
    if (inst[`name${lang}`] && typeof inst[`name${lang}`] === 'string') {
      return inst[`name${lang}`];
    }
    
    // Try shortName as fallback
    if (typeof inst.shortName === 'string' && inst.shortName.trim()) {
      return inst.shortName;
    }
    
    if (inst.shortName && typeof inst.shortName === 'object') {
      const langLower = lang.toLowerCase();
      if (inst.shortName[langLower]) return inst.shortName[langLower];
      if (inst.shortName[lang]) return inst.shortName[lang];
      const firstLang = Object.keys(inst.shortName)[0];
      if (firstLang && inst.shortName[firstLang]) return inst.shortName[firstLang];
    }
    
    if (inst[`shortName${lang}`] && typeof inst[`shortName${lang}`] === 'string') {
      return inst[`shortName${lang}`];
    }
    
    return null;
  }, [lang]);
  
  const handleInstitutionClick = React.useCallback(() => {
    // Only navigate if we have a valid institution ID and name
    const institutionId = institution?._id;
    const institutionName = getInstitutionName(institution);
    
    if (institutionId && institutionName && institutionName !== 'Unknown Institution') {
      const slug = slugify(institutionName);
      const path = institutionType === 'college' ? 'colleges' : 'universities';
      navigate(`/education/${path}/${slug}/${institutionId}`);
    }
  }, [institution, navigate, institutionType, getInstitutionName]);

  const institutionName = getInstitutionName(institution);
  
  // If we can't get a name, try to use address or ID as fallback
  const displayName = institutionName || 
    institution?.contactInfo?.katottgName || 
    institution?.contactInfo?.address || 
    (institution?._id ? `Institution ${institution._id.slice(-6)}` : 'Unknown Institution');
  
  const address = institution?.contactInfo?.katottgName 
    ? `${institution.contactInfo.katottgName}${institution.contactInfo?.address ? `, ${institution.contactInfo.address}` : ''}`
    : institution?.contactInfo?.address || '';

  // Don't render institution header if we have no valid identifying information
  // If no valid institution, just render programs without header
  if (!institution?._id || !institutionName || institutionName === 'Unknown Institution') {
    return <ProgramsGroup>{children}</ProgramsGroup>;
  }

  return (
    <InstitutionGroupWrapper>
      <InstitutionHeader onClick={handleInstitutionClick} style={{ cursor: institution?._id ? 'pointer' : 'default' }}>
        <InstitutionName>{displayName}</InstitutionName>
        {address && address !== displayName && <InstitutionInfo>{address}</InstitutionInfo>}
      </InstitutionHeader>
      <ProgramsGroup>{children}</ProgramsGroup>
    </InstitutionGroupWrapper>
  );
};

const EducationProgramsPage = ({ institutionType }) => {
  const helmet = useTranslate('HELMET');
  const t = useTranslate('EducationPrograms');
  const isMobile = useSelector((state) => state.app.isMobile);
  const [filtersView, setFiltersView] = React.useState(true);
  const dispatch = useDispatch();
  const [filterOptions, setFilterOptions] = React.useState({});
  const [autoLocationApplied, setAutoLocationApplied] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [sortAnchorEl, setSortAnchorEl] = React.useState(null);
  const sortMenuOpen = Boolean(sortAnchorEl);
  const [showAllDirections, setShowAllDirections] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get user's location from IP
  const { location: userIpLocation, loading: userLocationLoading } = useUserLocation();

  // Use institutionType in key to force re-initialization when type changes
  const dataListKey = `${EducationProgramsEndPoint.sourceName}_${institutionType || 'all'}`;
  
  // Read initial filters from URL
  const initialFilters = React.useMemo(() => {
    const urlParams = parseQS(location.search);
    const initialFiltersObj = institutionType ? { institutionType } : {};
    
    if (urlParams.filters) {
      if (urlParams.filters.degree) {
        initialFiltersObj.degree = urlParams.filters.degree;
      }
      if (urlParams.filters.educationForm) {
        initialFiltersObj.educationForm = urlParams.filters.educationForm;
      }
      if (urlParams.filters.language) {
        initialFiltersObj.language = urlParams.filters.language;
      }
      if (urlParams.filters.directions) {
        initialFiltersObj.directions = urlParams.filters.directions;
      }
      if (urlParams.filters.budgetAvailable !== undefined) {
        initialFiltersObj.budgetAvailable = urlParams.filters.budgetAvailable === 'true';
      }
      if (urlParams.filters.search) {
        initialFiltersObj.search = urlParams.filters.search;
      }
      // Location type filter
      if (urlParams.filters.locationType) {
        initialFiltersObj.locationType = urlParams.filters.locationType;
      }
    }
    
    // If no location filter is specified in URL, don't apply any location filtering
    // This ensures all programs are shown by default
    if (!initialFiltersObj.locationType) {
      initialFiltersObj.ignore_location = 'true';
    }
    
    return initialFiltersObj;
  }, [location.search, institutionType]);
  
  // Enable grouping by institution for universities page
  const shouldGroupByInstitution = institutionType === 'university';
  
  const { actions, data, loading, page, perPage, count, sort, filters } =
    useDataList({
      ...EducationProgramsEndPoint,
      sourceName: dataListKey
    }, {
      filters: initialFilters,
      groupBy: shouldGroupByInstitution ? { institution: true } : {}
    });

  const filtersRef = React.useRef(filters);
  React.useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Update URL when filters change - defined first as other callbacks depend on it
  const updateURLWithFilters = React.useCallback((newFilters) => {
    const urlParams = new URLSearchParams(location.search);
    
    // Remove existing filter params
    Array.from(urlParams.keys()).forEach(key => {
      if (key.startsWith('filters[')) {
        urlParams.delete(key);
      }
    });
    
    // Add new filter params
    Object.entries(newFilters).forEach(([key, value]) => {
      if (key === 'institutionType') return; // Don't add institutionType to URL as it's in the route
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((v) => urlParams.append(`filters[${key}]`, v));
        } else if (typeof value === 'string' && value.includes(',')) {
          value.split(',').forEach((v) => {
            if (v.trim()) {
              urlParams.append(`filters[${key}]`, v.trim());
            }
          });
        } else {
          urlParams.set(`filters[${key}]`, String(value));
        }
      }
    });
    
    const newSearch = urlParams.toString();
    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const handleOpenChange = React.useCallback(
    (isOpen) => {
      if (!isOpen) {
        setQuery('');
        actions.onSearchChange('');
      }
    },
    [actions]
  );

  const handleChange = React.useCallback((e) => setQuery(e.target.value), []);

  const handleSearch = React.useCallback(() => {
    actions.onSearchChange(query);
    const nextFilters = { ...filters, search: query };
    updateURLWithFilters(nextFilters);
  }, [actions, query, filters, updateURLWithFilters]);

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const handleToggleFiltersView = React.useCallback(() => {
    if (!isMobile) return;
    setFiltersView((prev) => !prev);
  }, [isMobile]);

  const handleChangeFilter = React.useCallback(
    (name, value) => {
      let nextFilters;
      
      if (typeof value === 'boolean') {
        nextFilters = { ...filters };
        if (filters[name] === value) {
          delete nextFilters[name];
        } else {
          nextFilters[name] = value;
        }
      } else {
        const source = (filters[name] || '').split(',') || [];
        const result = source.includes(value)
          ? source.filter((v) => v !== value)
          : [...source, value];

        nextFilters = {
          ...filters,
          [name]: result.filter(Boolean).join(',')
        };
      }

      actions.onFilterChange(nextFilters, true);
      updateURLWithFilters(nextFilters);
    },
    [actions, filters, updateURLWithFilters]
  );

  const handleLocationTypeChange = React.useCallback(
    (locationType) => {
      const nextFilters = { ...filters };
      // Remove ignore_location when user selects a specific location type
      delete nextFilters.ignore_location;
      
      if (filters.locationType === locationType) {
        // If clicking the same option, remove the filter (show all programs)
        delete nextFilters.locationType;
        nextFilters.ignore_location = 'true';
      } else {
        // Set the new location type
        nextFilters.locationType = locationType;
      }
      actions.onFilterChange(nextFilters, true);
      updateURLWithFilters(nextFilters);
    },
    [filters, actions, updateURLWithFilters]
  );

  const handleClearFilters = React.useCallback(() => {
    const clearedFilters = institutionType ? { institutionType } : {};
    clearedFilters.ignore_location = 'true'; // Show all programs by default
    actions.onFilterChange(clearedFilters, true);
    updateURLWithFilters(clearedFilters);
  }, [actions, institutionType, updateURLWithFilters]);

  const handleSort = React.useCallback(
    (column, direction) => {
      if (sort[column] && sort[column] === direction) {
        actions.onSortChange(column, null);
        setSortAnchorEl(null);
        return;
      }
      actions.onSortChange(column, direction);
      setSortAnchorEl(null);
    },
    [actions, sort]
  );

  const CheckBoxItem = React.useMemo(
    () =>
      React.memo(({ item, checked, onChange }) => (
        <FormGroupStyled>
          <FormControlLabelStyled
            control={
              <CheckboxStyled checked={checked} onChange={onChange} icon={<CheckboxIcon />} />
            }
            label={item}
          />
        </FormGroupStyled>
      )),
    []
  );

  const limitReached = React.useMemo(() => count <= page * perPage, [count, page, perPage]);

  const sortValues = React.useMemo(
    () => [
      { column: 'programName', direction: 'asc', name: t('ByNameAsc') },
      { column: 'programName', direction: 'desc', name: t('ByNameDesc') },
      { column: 'price', direction: 'asc', name: t('ByPriceAsc') },
      { column: 'price', direction: 'desc', name: t('ByPriceDesc') }
    ],
    [t]
  );

  const directionTags = React.useMemo(
    () =>
      (filterOptions?.directions || []).map((topic) => {
        // Tolerate both the legacy string shape and the new {slug,name} object
        // (depending on which backend version is deployed).
        const value = topic?.slug ?? topic;
        const selected = (filters.directions || '').split(',').includes(value);
        return (
          <TagsButton
            key={value}
            variant={selected ? 'contained' : 'outlined'}
            onClick={() => handleChangeFilter('directions', value)}>
            {topic?.name ?? topic}
          </TagsButton>
        );
      }),
    [filterOptions?.directions, filters.directions, handleChangeFilter]
  );

  // Limit number of visible directions (same as Courses page)
  // Mobile: ~2-3 tags per row, 12 rows = ~24-36 tags
  // Desktop: ~6 tags per row, 5 rows = ~30 tags
  const maxRows = isMobile ? 12 : 5;
  const estimatedTagsPerRow = isMobile ? 3 : 6;
  const maxVisibleTags = maxRows * estimatedTagsPerRow;
  const hasMoreDirections = directionTags.length > maxVisibleTags && !showAllDirections;
  
  // Reset showAllDirections when directions change
  React.useEffect(() => {
    setShowAllDirections(false);
  }, [filterOptions?.directions]);

  // Check if data is grouped by institution (has institution and programs properties)
  const isGroupedData = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return false;
    return data[0]?.institution && Array.isArray(data[0]?.programs);
  }, [data]);

  const programCards = React.useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    if (isGroupedData) {
      // Data is grouped by institution
      return data.map((group, groupIdx) => {
        const { institution, programs } = group;
        if (!institution || !programs || !Array.isArray(programs) || programs.length === 0) return null;
        
        // Skip groups with invalid institutions (no ID or no name)
        const institutionId = institution?._id;
        const hasValidInstitution = institutionId && institutionId.toString && institutionId.toString() !== 'undefined';
        
        if (!hasValidInstitution) {
          // If institution is invalid, just render programs without grouping
          return programs.map((program, programIdx) => (
            <EducationProgramCard key={program._id || `${groupIdx}-${programIdx}`} item={program} />
          ));
        }
        
        return (
          <InstitutionGroup key={institutionId.toString() || groupIdx} institution={institution} institutionType={institutionType}>
            {programs.map((program, programIdx) => (
              <EducationProgramCard key={program._id || `${groupIdx}-${programIdx}`} item={program} />
            ))}
          </InstitutionGroup>
        );
      }).filter(Boolean);
    } else {
      // Regular flat list of programs
      return data.map((item, idx) => <EducationProgramCard key={item._id || idx} item={item} />);
    }
  }, [data, isGroupedData, institutionType]);

  // Fetch filter options with current filters applied
  React.useEffect(() => {
    // Prepare filters for the API call (exclude budgetAvailable from filter options query)
    const filtersForOptions = { ...(filtersRef.current || {}) };
    // Remove budgetAvailable from filters when fetching options to show all available options
    delete filtersForOptions.budgetAvailable;
    
    // Convert comma-separated strings to arrays for API
    const normalizedFilters = {};
    Object.entries(filtersForOptions).forEach(([key, value]) => {
      if (value && typeof value === 'string' && value.includes(',')) {
        normalizedFilters[key] = value.split(',').filter(Boolean);
      } else if (value !== undefined && value !== null && value !== '') {
        normalizedFilters[key] = value;
      }
    });

    dispatch(getEducationProgramsFilterOptions(normalizedFilters)).then((res) => {
      if (res && typeof res === 'object' && res.data) {
        setFilterOptions(res.data);
      } else if (res && typeof res === 'object') {
        setFilterOptions(res);
      } else {
        setFilterOptions({});
      }
    });
  }, [dispatch, filters.degree, filters.educationForm, filters.language, filters.directions, filters.institutionType]);

  // Auto-apply location type filter when user location is detected
  React.useEffect(() => {
    // Skip if already applied or still loading
    if (autoLocationApplied || userLocationLoading) {
      return;
    }
    
    // Check if locationType was explicitly set in URL (user chose a specific filter)
    const urlParams = parseQS(location.search);
    if (urlParams.filters?.locationType) {
      setAutoLocationApplied(true);
      return;
    }

    // If user is in Ukraine (UA) and using default (ignore_location), 
    // optionally switch to 'local' to show programs in user's region
    if (userIpLocation?.countryCode === 'ua' && filters.ignore_location) {
      // Remove ignore_location and set local filter for Ukrainian users
      const nextFilters = { ...filters, locationType: 'local' };
      delete nextFilters.ignore_location;
      actions.onFilterChange(nextFilters, true);
      updateURLWithFilters(nextFilters);
    }
    setAutoLocationApplied(true);
  }, [userIpLocation, userLocationLoading, autoLocationApplied, filters, actions, location.search, updateURLWithFilters]);

  const { sentinelRef } = useInfiniteScroll({
    isLoading: loading,
    hasMore: !limitReached,
    onLoadMore: handleChangePage,
    rootMargin: '200px 0px',
    threshold: 0
  });

  // Check if we're on the /programs path
  const isProgramsPath = location.pathname.includes('/programs');

  // Determine meta tags based on institution type and path
  const getMetaTags = React.useMemo(() => {
    if (institutionType === 'college') {
      if (isProgramsPath) {
        return {
          title: helmet('collegesProgramsTitle'),
          description: helmet('collegesProgramsDescription'),
          ogTitle: helmet('collegesProgramsOgTitle'),
          ogDescription: helmet('collegesProgramsOgDescription')
        };
      }
      return {
        title: helmet('collegesTitle'),
        description: helmet('collegesDescription'),
        ogTitle: helmet('collegesOgTitle'),
        ogDescription: helmet('collegesOgDescription')
      };
    }
    
    if (institutionType === 'university') {
      if (isProgramsPath) {
        return {
          title: helmet('universitiesProgramsTitle'),
          description: helmet('universitiesProgramsDescription'),
          ogTitle: helmet('universitiesProgramsOgTitle'),
          ogDescription: helmet('universitiesProgramsOgDescription')
        };
      }
      return {
        title: helmet('universitiesTitle'),
        description: helmet('universitiesDescription'),
        ogTitle: helmet('universitiesOgTitle'),
        ogDescription: helmet('universitiesOgDescription')
      };
    }
    
    return {
      title: helmet('educationProgramsTitle'),
      description: helmet('educationProgramsDescription'),
      ogTitle: helmet('educationProgramsOgTitle'),
      ogDescription: helmet('educationProgramsOgDescription')
    };
  }, [institutionType, isProgramsPath, helmet]);

  // Breadcrumbs
  const breadcrumbs = React.useMemo(() => {
    const items = [
      { link: '/', label: 'Home' },
      { link: '/education', label: 'EducationProgramsPage' }
    ];

    // Add institutions page (universities or colleges) if we're on programs path
    if (isProgramsPath && institutionType) {
      const institutionsPath = institutionType === 'college' 
        ? '/education/colleges' 
        : '/education/universities';
      const institutionsLabel = institutionType === 'college'
        ? t('TitleCollegesInstitutions')
        : t('TitleUniversitiesInstitutions');
      
      items.push({ 
        link: institutionsPath, 
        label: institutionsLabel,
        raw: true
      });
    }

    // Add current page (programs page)
    if (isProgramsPath) {
      const programsLabel = institutionType === 'college'
        ? t('TitleColleges')
        : t('TitleUniversities');
      items.push({ 
        link: location.pathname, 
        label: programsLabel,
        raw: true
      });
    }

    return items;
  }, [institutionType, isProgramsPath, location.pathname, t]);

  return (
    <>
      <Helmet
        meta={getMetaTags}
      />
      <MainLayout>
        <Header />
        <Background>
          <Content>
            <React.Suspense fallback={null}>
              <BreadcrumbsWrapper>
                <Breadcrumbs breadcrumbs={breadcrumbs} />
              </BreadcrumbsWrapper>
            </React.Suspense>
          <Card>
            <Title variant="h1">
              {institutionType === 'college' 
                ? t('TitleColleges') 
                : institutionType === 'university' 
                ? t('TitleUniversities') 
                : t('Title')}
            </Title>

            <Actions>
              <ResultsLabel>
                {institutionType === 'college' 
                  ? t('SearchPromptColleges') 
                  : t('SearchPrompt')}
              </ResultsLabel>
              {!isMobile ? (
                <SearchToggleInput
                  query={query}
                  onChange={handleChange}
                  onSearch={handleSearch}
                  onOpenChange={handleOpenChange}
                  placeholder={t('SearchPlaceholder')}
                  openAriaLabel={t('OpenSearch')}
                  closeAriaLabel={t('CloseSearch')}
                  searchAriaLabel={t('Search')}
                  fullWidth={true}
                />
              ) : null}
            </Actions>

            <DirectionsContainer showAll={showAllDirections} maxRows={maxRows} isMobile={isMobile}>
              {directionTags}
            </DirectionsContainer>
            {hasMoreDirections && (
              <ShowAllButton
                onClick={() => setShowAllDirections(true)}
                variant="text"
                fullWidth>
                {t('ShowAll')}
              </ShowAllButton>
            )}
          </Card>
        </Content>

        <Content style={{ paddingTop: 10 }}>
          <Wrapper>
            <LeftSidebar>
              <ResultsWrapper>
                {data && data.length === 0 ? (
                  <ResultsLabel>{t('NoData')}</ResultsLabel>
                ) : (
                  <>
                    <ResultsLabelContainer>
                      <ResultsLabel>
                        {institutionType === 'college' 
                          ? t('FoundResultsColleges', { count }) 
                          : t('FoundResults', { count })}
                      </ResultsLabel>
                      {loading && <CircularProgress size={16} />}
                    </ResultsLabelContainer>
                    <ResultsControls>
                      {isMobile ? (
                        <SearchToggleInput
                          query={query}
                          onChange={handleChange}
                          onSearch={handleSearch}
                          onOpenChange={handleOpenChange}
                          placeholder={t('SearchPlaceholder')}
                          openAriaLabel={t('OpenSearch')}
                          closeAriaLabel={t('CloseSearch')}
                          searchAriaLabel={t('Search')}
                        />
                      ) : null}
                      <IconButtonStyled
                        aria-label={t('Sort')}
                        onClick={(e) => setSortAnchorEl(e.currentTarget)}>
                        <SortIcon />
                      </IconButtonStyled>
                    </ResultsControls>
                    <Menu
                      anchorEl={sortAnchorEl}
                      open={sortMenuOpen}
                      onClose={() => setSortAnchorEl(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                      {sortValues.map((item) => (
                        <MenuItem
                          key={`${item.column}-${item.direction}`}
                          selected={sort[item.column] === item.direction}
                          onClick={() => handleSort(item.column, item.direction)}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </ResultsWrapper>

              {isGroupedData ? (
                <div>{programCards}</div>
              ) : (
                <List>{programCards}</List>
              )}

              {!data && loading ? <CardGridSkeleton count={6} /> : null}

            {loading || !data ? <LinearProgress /> : null}

              {limitReached ? null : (
                <div ref={sentinelRef} aria-label={t('LoadMore')} style={{ height: 1 }} />
              )}
            </LeftSidebar>

            <RightSidebar>
              <Filters>
                <FiltersTitle onClick={handleToggleFiltersView}>
                  <FiltersIcon />
                  {t('filtersTitle')}
                </FiltersTitle>

                {filtersView ? (
                  <FilterWrapper>
                    {/* Location type filter */}
                    <FiltersHeadline>{t('LocationHeadline')}</FiltersHeadline>
                    <CheckBoxItem
                      item={t('LocationTypeLocal')}
                      checked={filters.locationType === 'local'}
                      onChange={() => handleLocationTypeChange('local')}
                    />
                    <CheckBoxItem
                      item={t('LocationTypeNational')}
                      checked={filters.locationType === 'national'}
                      onChange={() => handleLocationTypeChange('national')}
                    />
                    <CheckBoxItem
                      item={t('LocationTypeInternational')}
                      checked={filters.locationType === 'international'}
                      onChange={() => handleLocationTypeChange('international')}
                    />

                    <FiltersHeadline>{t('DegreeHeadline')}</FiltersHeadline>
                    {(filterOptions?.degrees || []).map((degree) => (
                      <CheckBoxItem
                        key={degree}
                        item={degree}
                        checked={(filters.degree || '').split(',').includes(degree)}
                        onChange={() => handleChangeFilter('degree', degree)}
                      />
                    ))}

                    <FiltersHeadline>{t('StudyFormHeadline')}</FiltersHeadline>
                    {(() => {
                      const orderedForms = ['fullTime', 'extramural', 'distance', 'evening'];
                      const availableForms = filterOptions?.educationForms || [];
                      const labelMap = {
                        fullTime: 'Денна',
                        evening: 'Вечірня',
                        extramural: 'Заочна',
                        distance: 'Дистанційна'
                      };

                      return orderedForms
                        .filter((form) => availableForms.includes(form))
                        .map((form) => {
                          const label = labelMap[form] || form;
                          return (
                            <CheckBoxItem
                              key={form}
                              item={label}
                              checked={(filters.educationForm || '').split(',').includes(form)}
                              onChange={() => handleChangeFilter('educationForm', form)}
                            />
                          );
                        });
                    })()}

                    <FiltersHeadline>{t('LanguageHeadline')}</FiltersHeadline>
                    {(filterOptions?.language || []).map((lang) => {
                      const label =
                        lang === 'Ukrainian'
                          ? t('LanguageUA')
                          : lang === 'English'
                            ? t('LanguageEN')
                            : lang;
                      return (
                        <CheckBoxItem
                          key={lang}
                          item={label}
                          checked={(filters.language || '').split(',').includes(lang)}
                          onChange={() => handleChangeFilter('language', lang)}
                        />
                      );
                    })}

                    <CheckBoxItem
                      item={t('budgetAvailableYes')}
                      checked={filters.budgetAvailable === true}
                      onChange={() => handleChangeFilter('budgetAvailable', true)}
                    />


                    <ClearFilterActionButton
                      variant="outlined"
                      fullWidth={true}
                      onClick={handleClearFilters}>
                      {t('ClearAllFilters')}
                    </ClearFilterActionButton>
                  </FilterWrapper>
                ) : null}
              </Filters>
            </RightSidebar>
          </Wrapper>
          <TrollGreetingsStyled src={TrollGreetings} alt="" />
        </Content>
        <TrollGreetingsStyledTop src={TrollGreetings} alt="" />
        <BgLeftStyled />
        <BgRightStyled />
        </Background>
        <Footer />
      </MainLayout>
    </>
  );
};

export default EducationProgramsPage;
